import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { PICKUP_FORM_URL } from '@/config/forms';
import { MAX_QTY, usePickupBag } from '@/hooks/usePickupBag';
import type { BagLine } from '@/hooks/usePickupBag';
import RequestSummary from './RequestSummary';

export type SendStatus = 'idle' | 'submitting' | 'success' | 'error';
export type Submitted = { lines: BagLine[]; date: string };
type Errors = Partial<Record<'bag' | 'date' | 'name' | 'contact' | 'email' | 'phone' | 'notes', string>>;

const toISO = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const field = 'block w-full box-border h-[50px] mt-2 bg-transparent border-0 border-b border-[rgba(23,21,21,0.25)] px-0 text-[15px] text-[#171515] placeholder:text-[#a39e98] focus:outline-none focus:border-[#171515] transition-colors';
const label = 'fold-label text-[#5d5853]';

function Err({ id, msg }: { id: string; msg?: string }) {
  return msg ? <p id={id} role="alert" className="mt-2 text-xs text-[#171515] flex items-center gap-1.5"><i className="ri-error-warning-line" aria-hidden="true"></i>{msg}</p> : null;
}

/** Review validates locally; Send posts to the project's Native Form (see src/config/forms.ts). */
export default function PickupRequest() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = toISO(tomorrow);

  const { items, lines, clearBag } = usePickupBag();
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [checked, setChecked] = useState(false);
  const [status, setStatus] = useState<SendStatus>('idle');
  const [sendError, setSendError] = useState('');
  const [submitted, setSubmitted] = useState<Submitted | null>(null);
  const statusRef = useRef<SendStatus>('idle');
  statusRef.current = status;

  // Any bag change after review invalidates it (but not the bag clear that follows a confirmed success).
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    if (statusRef.current === 'submitting') return;
    if (statusRef.current === 'success') {
      if (items.length) { setStatus('idle'); setSubmitted(null); }
      return;
    }
    setChecked(false);
    if (statusRef.current === 'error') { setStatus('idle'); setSendError(''); }
    setErrors((prev) => (prev.bag && items.length ? { ...prev, bag: undefined } : prev));
  }, [items]);

  const validate = (): Errors => {
    const e: Errors = {};
    if (lines.length === 0 || lines.length !== items.length) e.bag = 'Add at least one bake to your bag.';
    else if (lines.some((l) => !Number.isInteger(l.quantity) || l.quantity < 1 || l.quantity > MAX_QTY)) e.bag = `Each bake needs a quantity from 1 to ${MAX_QTY}.`;
    if (!date) e.date = 'Choose a collection date.';
    else if (date < minDate) e.date = 'Requests need at least a day\u2019s notice.';
    else if (new Date(`${date}T12:00:00`).getDay() === 1) e.date = 'We\u2019re closed on Mondays.';
    if (!name.trim()) e.name = 'Tell us who\u2019s collecting.';
    if (!email.trim() && !phone.trim()) e.contact = 'Add an email or a phone number.';
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'That email doesn\u2019t look right.';
    if (phone.trim() && phone.replace(/\D/g, '').length < 7) e.phone = 'That number looks too short.';
    if (notes.length > 500) e.notes = 'Keep notes under 500 characters.';
    return e;
  };

  const onCheck = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    setChecked(Object.keys(e).length === 0);
  };

  const touch = () => {
    if (status === 'submitting') return;
    if (checked) setChecked(false);
    if (status !== 'idle') { setStatus('idle'); setSendError(''); setSubmitted(null); }
  };

  const onSend = async () => {
    if (status === 'submitting' || !PICKUP_FORM_URL) return;
    const formEl = document.getElementById('pickup-request-form') as HTMLFormElement | null;
    if (!formEl) return;
    const fd = new FormData(formEl);
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); setChecked(false); return; }
    const snapshot = lines.map((l) => ({ ...l }));
    const body = new URLSearchParams();
    // One record holds the whole bag. No single-item product_* fields and no authoritative total are sent.
    body.set('items_summary', snapshot.map((l) => `${l.product.name} \u00d7 ${l.quantity}`).join('\n'));
    body.set('items_json', JSON.stringify(snapshot.map((l) => ({ slug: l.product.slug, name: l.product.name, quantity: l.quantity, indicative_unit_price: l.product.price }))));
    body.set('item_count', String(snapshot.length));
    body.set('total_quantity', String(snapshot.reduce((s, l) => s + l.quantity, 0)));
    body.set('preferred_date', String(fd.get('date') ?? date));
    body.set('name', String(fd.get('name') ?? name).trim());
    body.set('email', String(fd.get('email') ?? email).trim());
    body.set('phone', String(fd.get('phone') ?? phone).trim());
    body.set('notes', String(fd.get('notes') ?? notes));
    body.set('price_note', 'Unit prices in items_json are indicative estimates only, not an order total. Payment at the counter.');
    setStatus('submitting');
    setSendError('');
    try {
      const response = await fetch(PICKUP_FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: unknown } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const detail = typeof parsed?.meta?.detail === 'string' ? parsed.meta.detail : '';
      const serverMsg = parsed?.meta?.message || parsed?.message || detail || responseText;
      if (response.ok && parsed?.code === 'OK' && !/spam/i.test(`${serverMsg}`)) {
        setSubmitted({ lines: snapshot, date: String(fd.get('date') ?? date) });
        setStatus('success');
        setChecked(false);
        clearBag();
      } else {
        setStatus('error');
        setSendError(serverMsg ? `The request wasn\u2019t accepted: ${String(serverMsg).slice(0, 160)}` : 'The request wasn\u2019t accepted. Please try again.');
      }
    } catch {
      setStatus('error');
      setSendError('We couldn\u2019t reach the server. Check your connection and try again.');
    }
  };

  return (
    <section id="pickup" className="bg-[#F5F1EB] text-[#171515] py-20 md:py-32 scroll-mt-20" aria-labelledby="pickup-title">
      <div className="fold-shell">
        <header className="grid grid-cols-1 lg:grid-cols-12 gap-y-6 lg:gap-x-8 items-end pb-10 md:pb-12 border-b border-[#171515]">
          <div className="lg:col-span-8 min-w-0">
            <p className="fold-label text-[#8A8580]">Pickup request</p>
            <h2 id="pickup-title" className="mt-5 fold-serif uppercase leading-[0.92] tracking-[-0.03em] text-[11vw] sm:text-[8vw] lg:text-[5vw] xl:text-[4.8rem]">
              Something good, <span className="italic">set aside.</span>
            </h2>
          </div>
          <p className="lg:col-span-4 text-[15px] leading-[1.75] text-[#45403c] max-w-[400px]">
            Fill your bag, tell us when you&apos;d like to collect it, and we&apos;ll prepare your request for review.
          </p>
        </header>

        <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-y-12 lg:gap-x-8 items-start">
        <form id="pickup-request-form" data-readdy-form noValidate onSubmit={onCheck} onChange={touch} className="lg:col-span-7 min-w-0 flex flex-col gap-8">
          <div className="min-w-0">
            <p className={label}>Bakes</p>
            <p className="mt-3 pb-3 border-b border-[rgba(23,21,21,0.25)] text-[15px] text-[#45403c] flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
              <span>{lines.length ? `${lines.length} ${lines.length === 1 ? 'bake' : 'bakes'} in your bag — edit them in the summary.` : 'Your bag is empty.'}</span>
              <Link to="/collection" className="fold-label fold-link whitespace-nowrap cursor-pointer">Add from the collection</Link>
            </p>
            <Err id="pr-bag-err" msg={errors.bag} />
          </div>

          <div className="min-w-0">
            <label htmlFor="pr-date" className={label}>Collection date</label>
            <input id="pr-date" name="date" type="date" min={minDate} value={date} onChange={(e) => setDate(e.target.value)} aria-invalid={!!errors.date} aria-describedby="pr-date-hint pr-date-err" className={`${field} cursor-pointer`} />
            <p id="pr-date-hint" className="mt-2 text-xs text-[#8A8580]">From tomorrow. Closed Mondays.</p>
            <Err id="pr-date-err" msg={errors.date} />
          </div>

          <div className="min-w-0">
            <label htmlFor="pr-name" className={label}>Name</label>
            <input id="pr-name" name="name" type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Who's collecting" aria-invalid={!!errors.name} aria-describedby="pr-name-err" className={field} />
            <Err id="pr-name-err" msg={errors.name} />
          </div>

          <fieldset className="min-w-0" aria-describedby="pr-contact-err">
            <legend className={label}>Contact — email or phone</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
              <div className="min-w-0">
                <label htmlFor="pr-email" className="sr-only">Email</label>
                <input id="pr-email" name="email" type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" aria-invalid={!!errors.email || !!errors.contact} aria-describedby="pr-email-err" className={field} />
                <Err id="pr-email-err" msg={errors.email} />
              </div>
              <div className="min-w-0">
                <label htmlFor="pr-phone" className="sr-only">Phone</label>
                <input id="pr-phone" name="phone" type="tel" autoComplete="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" aria-invalid={!!errors.phone || !!errors.contact} aria-describedby="pr-phone-err" className={field} />
                <Err id="pr-phone-err" msg={errors.phone} />
              </div>
            </div>
            <Err id="pr-contact-err" msg={errors.contact} />
          </fieldset>

          <div className="min-w-0">
            <div className="flex items-baseline justify-between">
              <label htmlFor="pr-notes" className={label}>Notes <span className="normal-case tracking-normal text-[#8A8580]">(optional)</span></label>
              <span className="text-[11px] tabular-nums text-[#8A8580]">{notes.length}/500</span>
            </div>
            <textarea id="pr-notes" name="notes" rows={3} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Allergies, a time window, anything we should know" aria-invalid={!!errors.notes} aria-describedby="pr-notes-err" className={`${field} h-auto py-3 resize-none`} />
            <Err id="pr-notes-err" msg={errors.notes} />
          </div>

        </form>
        <div className="lg:col-span-5 min-w-0 lg:sticky lg:top-28">
          <RequestSummary submitted={submitted} date={date} formId="pickup-request-form" checked={checked} configured={!!PICKUP_FORM_URL} status={status} sendError={sendError} onSend={onSend} />
        </div>
        </div>
      </div>
    </section>
  );
}